"use client"

import ScreenLayout from "@/components/layout/screen-layout";
import { BgServicesComponentSvg } from "@/lib/images";
import Image, { StaticImageData } from "next/image";
import React, { useEffect, useState } from "react";
import { DynamicLinkButton } from "../../dynamic-link-button/dynamic-link-button";

type ButtonProps = {
    value: string;
    href: string;
    variant?: "secondary" | "dark_blue" | "default" | "outline";
    className?: string;
};

type SectionContent = {
    image: StaticImageData[];
    imageAlt?: string;
    heading: string;
    description: string;
    buttons?: ButtonProps[];
};

interface ReusableImageTextGridProps {
    section1: SectionContent;
    section2: SectionContent;
    backgroundColor?: string;
}

const ServicesImageWebGridSection: React.FC<ReusableImageTextGridProps> = ({
    section1,
    section2,
    backgroundColor = "#F7F8FC",
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Auto-rotate images
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === section1.image.length - 1 ? 0 : prevIndex + 1
            );
        }, 3000); // Change image every 3 seconds

        return () => clearInterval(interval);
    }, [section1.image.length]);

    return (
        <ScreenLayout className="px-4 sm:px-6 md:px-8">
            <div
                className={`grid 
        grid-cols-1 lg:grid-cols-2 
        auto-rows-auto 
        gap-x-4 sm:gap-x-6 md:gap-x-8 lg:gap-x-10 xl:gap-x-12
        gap-y-8 sm:gap-y-10 md:gap-y-12 lg:gap-y-10 xl:gap-y-12
        mt-4 sm:mt-6 md:mt-8 lg:mt-10 rounded-2xl
        p-4 sm:p-6 md:p-8 lg:p-10 xl:p-[62px]
        relative overflow-hidden`}
                style={{ backgroundColor }}
            >
                <div
                    className="                
            absolute                 
            left-1/2 -translate-x-1/2         
            z-0
            bg-no-repeat bg-center bg-cover
            md:-inset-y-1
            lg:-inset-y-10
            xl:-inset-y-28
          
            /* width changes */
            w-[100%]
            md:w-[100%]              
            lg:w-[100%]       
            xl:w-[100%]   
            2xl:w-[100%]"
                    style={{
                        backgroundImage: `url(${BgServicesComponentSvg.src})`,
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",
                    }}
                />
                {/* Section 1 Image */}
                <div className="order-2 lg:order-2 row-span-1 lg:row-span-2 z-10 flex justify-center lg:justify-start items-start">
                    <div className="w-full max-w-[300px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-full">
                        <div className="relative">
                            {section1.image.map((img, index) => (
                                <div
                                    key={index}
                                    className={`transition-opacity duration-500 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute top-0 left-0'}`}
                                >
                                    <Image
                                        src={img}
                                        alt={`${section1.imageAlt || ""} ${index + 1}`}
                                        className="w-full h-auto object-contain transition-transform duration-300 ease-in-out hover:scale-105"
                                        priority={index === 0}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Section 1 Text */}
                <div className="order-1 lg:order-1 z-10">
                    <div className="w-full lg:max-w-[90%] xl:max-w-[80%] space-y-3 sm:space-y-4 lg:space-y-6 text-center lg:text-left pt-6 lg:pt-8">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] xl:text-[55px] font-semibold text-custom-blue capitalize">
                            {section1.heading}
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl font-normal text-custom-blue opacity-80">
                            {section1.description}
                        </p>
                        {section1.buttons && (
                            <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                                {section1.buttons.map((btn, idx) => (
                                    <DynamicLinkButton key={idx} {...btn} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 2 Image */}
                <div className="order-3 lg:order-3 row-span-1 lg:row-span-2 z-10 flex justify-center lg:justify-start items-center">
                    <div className="w-full max-w-[300px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-full">
                        <Image
                            src={section2.image[0]}
                            alt={section2.imageAlt || ""}
                            className="w-full h-auto object-contain transition-transform duration-300 ease-in-out hover:scale-105"
                        />
                    </div>
                </div>

                {/* Section 2 Text */}
                <div className="order-4 lg:order-4 z-10">
                    <div className="w-full lg:max-w-[90%] xl:max-w-[80%] space-y-3 sm:space-y-4 lg:space-y-6 text-center lg:text-left">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] xl:text-[55px] font-semibold text-custom-blue capitalize">
                            {section2.heading}
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl font-normal text-custom-blue opacity-80">
                            {section2.description}
                        </p>
                        {section2.buttons && (
                            <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                                {section2.buttons.map((btn, idx) => (
                                    <DynamicLinkButton key={idx} {...btn} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ScreenLayout>
    );
};

export default ServicesImageWebGridSection;
